import axios from 'axios'

// Data Logger (Live) device API client.
// Mirrors projectsApi.js: same base URL / JWT-in-localStorage / Bearer header.
const API_URL = import.meta.env.VITE_API_URL || 'https://wqlive.ezxdemo.com'
const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'

const getAuthHeaders = () => {
  const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  }
}

const base = (projectId, deviceId) =>
  `${API_URL}${API_BASE_PATH}/project/${projectId}/device/${deviceId}`

export const deviceApi = {
  /** GET device header info (name, uid, firmware strings, last contact). */
  async fetchDevice(projectId, deviceId) {
    const res = await axios.get(`${base(projectId, deviceId)}/`, { headers: getAuthHeaders() })
    return res.data
  },

  /** GET Sample/Log/Transmit config grouped by sensor. */
  async fetchConfig(projectId, deviceId) {
    const res = await axios.get(`${base(projectId, deviceId)}/config/`, { headers: getAuthHeaders() })
    return res.data
  },

  /** Update device name (queues logger command #6). */
  async updateName(projectId, deviceId, name) {
    const res = await axios.post(
      `${base(projectId, deviceId)}/name/`,
      { name },
      { headers: getAuthHeaders() }
    )
    return res.data
  },

  /** Set device-wide Log and/or Transmit intervals (minutes). Queues the
   *  interval commands (51/7 for log, 8 for transmit). */
  async setIntervals(projectId, deviceId, { logMin, transmitMin } = {}) {
    const body = {}
    if (logMin != null && logMin !== '') body.log_min = logMin
    if (transmitMin != null && transmitMin !== '') body.transmit_min = transmitMin
    const res = await axios.post(
      `${base(projectId, deviceId)}/intervals/`,
      body,
      { headers: getAuthHeaders() },
    )
    return res.data
  },

  /** Apply the full Sample/Log/Transmit config (per-sensor intervals, device
   *  transmit interval, per-parameter transmit flags) from the Live "More" view. */
  async applyConfig(projectId, deviceId, payload) {
    const res = await axios.post(
      `${base(projectId, deviceId)}/config/apply/`,
      payload,
      { headers: getAuthHeaders() },
    )
    return res.data
  },

  /** Queue a device reset (command #4). type: 0 = RTU only, 1 = RTU + MAIN. */
  async resetDevice(projectId, deviceId, type = 1) {
    const res = await axios.post(`${base(projectId, deviceId)}/reset/`, { type }, { headers: getAuthHeaders() })
    return res.data
  },

  /** Queue an RTC clock sync (command #62). */
  async syncClock(projectId, deviceId) {
    const res = await axios.post(`${base(projectId, deviceId)}/sync-clock/`, {}, { headers: getAuthHeaders() })
    return res.data
  },

  /** Update a logger to the LATEST firmware of a type ('base' | 'rtu').
   *  The version is chosen server-side, so this can only ever go to the
   *  latest available (queues FTP firmware command #80). */
  async updateFirmware(projectId, deviceId, type) {
    const res = await axios.post(
      `${base(projectId, deviceId)}/firmware/update/`,
      { type },
      { headers: getAuthHeaders() },
    )
    return res.data
  },

  /** Cancel a QUEUED (not-yet-started) firmware update of a type
   *  ('base' | 'rtu') — removes the pending cmd #80 and clears the Queued
   *  progress so the card reverts to "Update to Latest". */
  async cancelFirmware(projectId, deviceId, type) {
    const res = await axios.post(
      `${base(projectId, deviceId)}/firmware/cancel/`,
      { type },
      { headers: getAuthHeaders() },
    )
    return res.data
  },

  /** Format Memory / Erase (command #66). type: 0 = erase log data,
   *  1 = erase sensor configuration. Destructive. */
  async formatMemory(projectId, deviceId, type) {
    const res = await axios.post(`${base(projectId, deviceId)}/format-memory/`, { type }, { headers: getAuthHeaders() })
    return res.data
  },
}

export default deviceApi
